import { EditDrawer } from '@app/icare/form/EditDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionFlowReturn } from '@pkg/form/useSectionFlow'
import { Alert, Flex, Space, Typography } from 'antd'
import type { ComponentProps, ComponentType, ReactNode } from 'react'

interface WarningConfig {
  title?: ReactNode
  description?: ReactNode
  confirmTitle?: ReactNode
  confirmDescription?: ReactNode
  confirmOkText?: ReactNode
  confirmCancelText?: ReactNode
}

type EntityDrawerFormLike = {
  AppForm: ComponentType<Record<string, unknown>>
  ProForm: ComponentType<Record<string, unknown>>
}

interface EntityDrawerShellDrawerLike {
  modal: ComponentProps<typeof EditDrawer>['modal']
  form: EntityDrawerFormLike
  flow: SectionFlowReturn
  isDirty?: boolean
  isLoading?: boolean
  isSaving: boolean
  canSubmit: boolean
  canProceed?: boolean
  error?: Error | null
  handleCancel: () => void
  handleSubmit: () => void
}

interface EntityDrawerShellProps {
  drawer: EntityDrawerShellDrawerLike
  /** Fixed drawer heading shown in `sequential` (add) mode. */
  title?: ComponentProps<typeof EditDrawer>['title']
  titleFor?: ComponentProps<typeof EditDrawer>['titleFor']
  showWarnings?: boolean
  warning?: WarningConfig
  children: ReactNode
}

export function EntityDrawerShell({
  drawer,
  title,
  titleFor,
  showWarnings,
  warning,
  children,
}: EntityDrawerShellProps) {
  const { modal, form, flow, isDirty, isLoading, isSaving, canSubmit, canProceed, error, handleCancel, handleSubmit } =
    drawer

  const resolvedWarning = {
    title: warning?.title ?? t`Achtung!`,
    description:
      warning?.description ??
      t`Diese Änderungen werden an den Stamminformationen vorgenommen und führen dazu, dass sie in allen Patientenakten sichtbar werden!`,
    confirmTitle: warning?.confirmTitle ?? t`Änderungen wirklich speichern?`,
    confirmDescription:
      warning?.confirmDescription ?? t`Sollen die Änderungen an den Stamminformationen wirklich vorgenommen werden?`,
    confirmOkText: warning?.confirmOkText ?? t`Wirklich speichern`,
    confirmCancelText: warning?.confirmCancelText ?? t`Abbrechen`,
  }

  return (
    <SectionFlow flow={flow} onSubmit={handleSubmit} onCancel={handleCancel}>
      <EditDrawer
        modal={modal}
        flow={flow}
        title={title}
        titleFor={titleFor}
        loading={isLoading}
        isDirty={isDirty}
        error={error}
        onClose={handleCancel}
        onDiscard={handleCancel}
        extra={({ requestClose }) => (
          <SectionFlow.Actions
            isSaving={isSaving}
            canSubmit={canSubmit}
            canProceed={canProceed}
            onCancel={requestClose}
            submitConfirm={
              showWarnings
                ? {
                    title: resolvedWarning.confirmTitle,
                    description: resolvedWarning.confirmDescription,
                    okText: resolvedWarning.confirmOkText,
                    cancelText: resolvedWarning.confirmCancelText,
                  }
                : undefined
            }
          />
        )}
      >
        <Flex vertical gap="large">
          {showWarnings && (
            <Alert
              type="error"
              showIcon
              title={
                <Space size="small">
                  <Typography.Text className="font-bold">{resolvedWarning.title}</Typography.Text>
                  {resolvedWarning.description}
                </Space>
              }
            />
          )}
          <form.AppForm>
            <form.ProForm layout="horizontal" labelAlign="left">
              {children}
            </form.ProForm>
          </form.AppForm>
        </Flex>
      </EditDrawer>
    </SectionFlow>
  )
}
